import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { FiCalendar } from "react-icons/fi";
import { FaRegUser } from "react-icons/fa6";
import { IoArrowBack } from "react-icons/io5";
import { SUBDOMAIN } from "../../../assets/meta/meta";
import QueryString from "qs";
import { getReservationSendGiftByPage } from "../../../api/service";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import useAutoLoadMore from "../../../hooks/useAutoLoadMore";
import PurchaseRecordModal from "../../../Components/modal/PurchaseRecordModal";
import DateChangeModal from "../../../Components/modal/DateChangeModal";
import ReservationGiftModal from "../../../Components/modal/ReservationGiftModal";
import {
  Card,
  CardHeader,
  StatusBadge,
  StatusDot,
  OrderInfo,
  OrderId,
  OrderDate,
  ProductName,
  DateInfo,
  SeatBadge,
  ActionButtons,
  DetailButton,
  ContentArea,
} from "../../../Components/Common";
import { LoadingSpinner, EmptyMessage, FilterBadges } from "../../../Components/Common";
import { getStatusText } from "../../../utils/statusUtils";
import { formatDate, formatOrderId } from "../../../utils/formatters";
import {
  getPurchaseGiftBadgeLabel,
  getReservationPurchaseActions,
} from "./actionVisibility.mjs";

const FILTER_OPTIONS = ["전체", "사용가능", "완료"];

const STATE_MAP: Record<string, string> = {
  전체: "",
  사용가능: "예약,입장,외출,퇴장",
  완료: "완료,노쇼",
};

export default function ReservationList() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("전체");
  const [purchaseRecordModal, setPurchaseRecordModal] = useState(false);
  const [dateChangeModal, setDateChangeModal] = useState(false);
  const [giftModal, setGiftModal] = useState(false);
  const [selectOrder, setSelectOrder] = useState<any>({});

  const filterStates = STATE_MAP[filter] ?? "";

  const query = useInfiniteQuery({
    queryKey: ["reservation-list", filterStates],
    queryFn: async ({ pageParam = 1 }) => {
      const qs = QueryString.stringify({
        filter_site: SUBDOMAIN,
        filter_states: filterStates,
      });
      const res = await getReservationSendGiftByPage({
        page: pageParam,
        queryString: qs ? `&${qs}` : "",
      });
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
  });

  const loadMoreRef = useAutoLoadMore({
    enabled: true,
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    onLoadMore: query.fetchNextPage,
    rootMargin: "160px 0px",
  });

  const allResults: any[] = query.data?.pages.flatMap((p: any) => p.results) ?? [];

  return (
    <React.Fragment>
      <MobileViewContainer>
        <div className="min-h-screen bg-gray-50 pb-24">
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center gap-2 bg-white px-4 py-3 shadow-[0_1px_0_#eee]">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
            >
              <IoArrowBack size={20} />
            </button>
            <h1 className="text-base font-semibold" style={{ fontFamily: "Gmarket-M" }}>
              예약 내역
            </h1>
          </div>

          <FilterBadges
            options={FILTER_OPTIONS}
            activeFilter={filter}
            onFilterChange={setFilter}
          />

          <ContentArea>
            {query.isLoading ? (
              <LoadingSpinner height="200px" />
            ) : allResults.length === 0 ? (
              <EmptyMessage message="예약 내역이 없습니다." />
            ) : (
              <>
                {allResults.map((order: any) => {
                  const actions = getReservationPurchaseActions(order);
                  const giftBadgeLabel = getPurchaseGiftBadgeLabel(order);

                  return (
                    <Card key={order.id}>
                      <CardHeader>
                        <StatusBadge status={order.state}>
                          <StatusDot status={order.state} />
                          {getStatusText(order.state)}
                        </StatusBadge>
                        <OrderInfo>
                          <OrderId>{formatOrderId(order.id, 12, "R-")}</OrderId>
                          <OrderDate>{formatDate(order.reservationDate)}</OrderDate>
                        </OrderInfo>
                      </CardHeader>

                      <div className="flex items-center gap-2">
                        <ProductName>{order.name}</ProductName>
                        {giftBadgeLabel ? (
                          <span className="inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-600">
                            {giftBadgeLabel}
                          </span>
                        ) : null}
                      </div>

                      <DateInfo>
                        <FiCalendar size={14} />
                        {order.reservationDate}
                        {order.reservationDate !== order.reservationEndDate && (
                          <> ~ {order.reservationEndDate}</>
                        )}
                        <span style={{ margin: "0 4px" }}>•</span>
                        <FaRegUser size={12} />
                        {order.numPeople}명
                      </DateInfo>

                      {!order.spot?.includes("virtual") && (
                        <SeatBadge>좌석: {order.spot}번</SeatBadge>
                      )}
                      {order.assigned_seat?.name ? (
                        <SeatBadge>배정 자리: {order.assigned_seat.name}</SeatBadge>
                      ) : null}

                      <ActionButtons>
                        {actions.showDetail && (
                          <DetailButton
                            onClick={() => {
                              setSelectOrder(order);
                              setPurchaseRecordModal(true);
                            }}
                          >
                            상세보기
                          </DetailButton>
                        )}
                      </ActionButtons>

                    </Card>
                  );
                })}

                {(query.hasNextPage || query.isFetchingNextPage) && (
                  <div
                    ref={loadMoreRef}
                    className="flex justify-center py-4 text-sm text-gray-500"
                    style={{ minHeight: 48 }}
                  >
                    {query.isFetchingNextPage
                      ? "예약 내역을 더 불러오는 중..."
                      : "아래로 스크롤하면 더 불러옵니다."}
                  </div>
                )}
              </>
            )}
          </ContentArea>
        </div>
      </MobileViewContainer>

      <PurchaseRecordModal
        setVisible={setPurchaseRecordModal}
        visible={purchaseRecordModal}
        info={selectOrder}
        setInfo={setSelectOrder}
        needRefresh={query.refetch}
        onDateChange={
          getReservationPurchaseActions(selectOrder).showDateChange
            ? () => {
                setPurchaseRecordModal(false);
                setDateChangeModal(true);
              }
            : undefined
        }
        onGift={
          getReservationPurchaseActions(selectOrder).showGift
            ? () => {
                setPurchaseRecordModal(false);
                setGiftModal(true);
              }
            : undefined
        }
        onEditOptions={
          getReservationPurchaseActions(selectOrder).showOptions
            ? () => {
                setPurchaseRecordModal(false);
                navigate(`/add-options-detail/${selectOrder.id}`);
              }
            : undefined
        }
      />
      <DateChangeModal
        visible={dateChangeModal}
        toggle={() => setDateChangeModal(!dateChangeModal)}
        targetData={selectOrder}
        refreshPage={query.refetch}
      />
      <ReservationGiftModal
        setVisible={setGiftModal}
        visible={giftModal}
        info={selectOrder}
        setInfo={setSelectOrder}
        needRefresh={query.refetch}
      />
    </React.Fragment>
  );
}
